/**
 * In-memory ring buffer of recent API correlation metadata for support / debugging.
 * Populated by the itinerary Axios response interceptor.
 */

const CORRELATION_BUFFER_MAX = 80;

export interface CorrelationDiagnosticEntry {
    "t":      string;
    "method": string;

    /** URL path only (no query string) to limit sensitive data in exports */
    "path":         string;
    "status":       number | null;
    "scope":        "planner" | "offRoute" | "unknown";
    "flowIdSent"?:  string;
    "flowId"?:      string;
    "traceId"?:     string;
    "traceparent"?: string;
}

const buffer: CorrelationDiagnosticEntry[] = [];

/**
 * Records one response (or error with response) for diagnostic export.
 */
export const appendCorrelationDiagnosticEntry = ( entry: CorrelationDiagnosticEntry ): void => {
    buffer.push( entry );
    if ( buffer.length > CORRELATION_BUFFER_MAX ) {
        buffer.splice( 0, buffer.length - CORRELATION_BUFFER_MAX );
    }
};

/**
 * Returns a shallow copy of buffered entries (oldest first).
 */
export const getCorrelationDiagnosticEntries = (): readonly CorrelationDiagnosticEntry[] => {
    return [ ...buffer ];
};

export interface CorrelationDiagnosticReport {
    "generatedAt": string;
    "appVersion":  string;
    "pathname":    string;
    "entries":     readonly CorrelationDiagnosticEntry[];
}

/**
 * Builds a JSON-serializable diagnostic report for copy / share / error reports.
 */
export const buildCorrelationDiagnosticReport = (): CorrelationDiagnosticReport => {
    const envVersion = import.meta.env.VITE_APP_VERSION;
    const buildVersion = typeof _APP_VERSION_ === "string" && _APP_VERSION_.length > 0
        ? _APP_VERSION_
        : undefined;
    const resolvedVersion = buildVersion ??
      ( typeof envVersion === "string" && envVersion.length > 0 ? envVersion : undefined ) ??
      "unknown";

    return {
        "generatedAt": new Date().toISOString(),
        "appVersion":  resolvedVersion,
        "pathname":    typeof window !== "undefined" ? window.location.pathname : "",
        "entries":     getCorrelationDiagnosticEntries(),
    };
};

/**
 * Compact JSON string for clipboard and mail body.
 */
export const buildCorrelationDiagnosticReportJson = (): string => {
    return JSON.stringify( buildCorrelationDiagnosticReport(), null, 2 );
};
